import * as React from 'react'
import { useIntl } from 'react-intl'
import { capitalize } from 'lodash'
import { CircularProgress, TableHead } from '@mui/material'
import Box from '@mui/material/Box'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableContainer from '@mui/material/TableContainer'
import TableFooter from '@mui/material/TableFooter'
import TablePagination from '@mui/material/TablePagination'
import TableRow from '@mui/material/TableRow'
import Paper from '@mui/material/Paper'
import IconButton from '@mui/material/IconButton'
import RoundedAddIcon from '../../../../assets/icons/RoundedAdd.svg'
import TablePaginationActions from '../../../../components/muiComponents/tablePaginationActions/TablePaginationActions'
import { StudentGroupState, StudentState } from '../../../../features/types'

interface Props {
    studentsData: StudentGroupState[]
    studentsInfo: StudentState[]
    isFetchingStudents: boolean
    isAddingStudent: boolean
    isLoadingStudentsInfo: boolean
    pageNumber: number
    totalCount: number
    page: number
    rowsPerPage: number
    addStudentToGroup: (courseUserEnrollId: number, courseSubjectId: number) => void
    setPage: (page: number) => void
    setRowsPerPage: (rowsPerPage: number) => void
}

const NoGroupStudentsTable: React.FC<Props> = ({
    studentsData = [],
    studentsInfo = [],
    pageNumber = 0,
    totalCount = 0,
    page = 0,
    rowsPerPage = 0,
    isFetchingStudents = false,
    isAddingStudent = false,
    isLoadingStudentsInfo = false,
    setPage,
    setRowsPerPage,
    addStudentToGroup,
}) => {
    const { formatMessage, locale } = useIntl()
    // Avoid a layout jump when reaching the last page with empty rows.
    // const emptyRows =
    //     page > 0
    //         ? Math.max(0, (1 + page) * rowsPerPage - studentsData.length)
    //         : 0
    const emptyRows =
        page > 0 ? Math.max(0, rowsPerPage - studentsData.length) : 0

    const handleChangePage = (
        event: React.MouseEvent<HTMLButtonElement> | null,
        newPage: number,
    ) => {
        setPage(newPage + 1)
    }

    const handleChangeRowsPerPage = (
        event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
    ) => {
        setRowsPerPage(parseInt(event.target.value, 10))
        setPage(0)
    }

    const getFullName = (userId: number) => {
        const student = studentsInfo.find((student) => student.id === userId)

        if (student) {
            return student.firstName + ' ' + student.lastName
        }
    }

    return (
        <Box>
            <TableContainer
                component={Paper}
                sx={{ border: 1, borderColor: 'grey.100', borderRadius: 2 }}
                elevation={0}
            >
                <Table
                    sx={{ minWidth: 500 }}
                    aria-label="custom pagination table"
                >
                    <TableHead sx={{ bgcolor: 'grey.100' }}>
                        <TableRow>
                            <TableCell>
                                {formatMessage({ id: 'fullName' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'group' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'course' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'subjects' })}
                            </TableCell>

                            <TableCell>
                                {formatMessage({ id: 'language' })}
                            </TableCell>

                            <TableCell align="right">
                                {formatMessage({ id: 'actions' })}
                            </TableCell>
                        </TableRow>
                    </TableHead>

                    {studentsData.length ? (
                        <TableBody>
                            {studentsData.map((row) => (
                                <TableRow key={row.userId}>
                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {isLoadingStudentsInfo ? (
                                            <CircularProgress size={20} />
                                        ) : (
                                            getFullName(row.userId)
                                        )}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {row.groupTitle}
                                    </TableCell>

                                    <TableCell
                                        component="th"
                                        scope="row"
                                    >
                                        {
                                            row[
                                                `courseTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell>
                                        {
                                            row[
                                                `subjectsTitle${capitalize(
                                                    locale,
                                                )}`
                                            ]
                                        }
                                    </TableCell>

                                    <TableCell>{row.language}</TableCell>

                                    <TableCell
                                        style={{ width: 60 }}
                                        align="center"
                                    >
                                        <IconButton
                                            size="small"
                                            sx={{ background: '#f3f4f5' }}
                                            disabled={isAddingStudent}
                                            onClick={() =>
                                                addStudentToGroup(
                                                    row?.userCourseEnrollId, row?.courseSubjectId
                                                )
                                            }
                                        >
                                            <img
                                                src={RoundedAddIcon}
                                                alt="add"
                                                style={{ cursor: 'pointer' }}
                                            />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}

                            {emptyRows > 0 && (
                                <TableRow style={{ height: 53 * emptyRows }}>
                                    <TableCell colSpan={6} />
                                </TableRow>
                            )}
                        </TableBody>
                    ) : (
                        <TableBody>
                            <TableRow>
                                {!isFetchingStudents && (
                                    <TableCell
                                        component="th"
                                        scope="row"
                                        align="center"
                                        colSpan={7}
                                    >
                                        {formatMessage({ id: 'noData' })}
                                    </TableCell>
                                )}
                            </TableRow>
                        </TableBody>
                    )}

                    <TableFooter>
                        <TableRow>
                            <TablePagination
                                rowsPerPageOptions={[
                                    5,
                                    10,
                                    25,
                                    { label: 'All', value: -1 },
                                ]}
                                colSpan={6}
                                count={totalCount}
                                rowsPerPage={rowsPerPage}
                                page={pageNumber}
                                SelectProps={{
                                    inputProps: {
                                        'aria-label': 'rows per page',
                                    },
                                    native: true,
                                }}
                                onPageChange={handleChangePage}
                                onRowsPerPageChange={handleChangeRowsPerPage}
                                ActionsComponent={TablePaginationActions}
                                sx={{ border: 0 }}
                            />
                        </TableRow>
                    </TableFooter>
                </Table>
            </TableContainer>
        </Box>
    )
}

export default NoGroupStudentsTable
